/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';

import { PlanState } from 'src/app/services/plan-state.service';

import { dateCache, DateCache, PlanAccessLevel, ProjectMember, TicketScale } from '@touchplan/common';
import { BehaviorSubject, Subject, Observable, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
	AnalyticsEventsService,
	analyticsEventsService,
} from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { IntercomService } from 'src/app/services/intercom.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import * as AppStrings from '@touchplan/strings';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';
import { SupportMenuLinks } from '../support-menu/support-links';
import { MatDialog } from '@angular/material/dialog';
import { OrganizationAppEditorComponent } from 'src/app/modules/organizations/components/organization-app-editor/organization-app-editor.component';
import { OrganizationService } from 'src/app/modules/organizations/services/organization.service';
import { ReportsComponent } from '../../../../modules/reports/components/report/reports.component';
import { HoverOverUserPreferencesService } from 'src/app/services/hover-over-preferences.service';
import { AuthService } from 'src/app/services/auth.service';
import { AppState } from 'src/app/services/app-state.service';
import { UserPreferenceStateService } from 'src/app/services/user-preference-state.service';
import { openImportDialog } from 'src/app/modules/import-tickets/import-dialog-opener';
import { OverviewProjectService } from 'src/app/services/overview-project.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { PlanPrintPanelRemoteControlService } from 'src/app/services/plan-print-panel-remote-control.service';
import { PlanTicketsFilterRemoteControlService } from 'src/app/services/plan-tickets-filter-remote-control.service';
import { DropdownMenuComponent } from 'src/app/common/utility-components/dropdown-menu/dropdown-menu.component';
import { FormControl } from '@angular/forms';
import { GranularEnabledService } from 'src/app/common/granular-time/granular-enabled.service';

@Component({
	selector: 'nav-bar-plan',
	templateUrl: './nav-bar-plan.component.html',
	styles: ['.nav-bar-dropdown-menu { width: 230px }'], // wider to fit new "TouchplanGO User Guide" in menu
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavBarPlanComponent implements OnInit, OnDestroy {
	protected dateCache: DateCache = dateCache;
	private shutdownSubject$: Subject<void> = new Subject();
	private userId: string = null;
	public lagBehindHide = false;
	public statusHidden$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public projectMembers: Map<string, ProjectMember>;
	public supportMenuLinks = SupportMenuLinks;
	public appStrings = AppStrings;
	public undoEnabled$: Observable<boolean>;
	public analyticsEventsService: AnalyticsEventsService;
	public widePullTicketEnabled$: Observable<boolean>;
	public isOrgAdmin$: Observable<boolean>;
	public isHoverOverEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public preferences$ = this.userPreferenceStateService.preferences$;
	public isPlanActionsHoverOverEnabled$: Observable<boolean> = this.featureFlagManager.getFlag$(
		DynamicFlagTypes.PLAN_ACTIONS_HOVER_OVER
	);
	private isPlanPrintEnabled$: Observable<boolean>;
	private printPlanClicks$ = new Subject<Event>();
	private planActionsMenu?: DropdownMenuComponent;
	private planActionsMenuSmall?: DropdownMenuComponent;

	public PlanAccessLevel = PlanAccessLevel;

	public isTicketImportV2Enabled$ = combineLatest([
		this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKET_IMPORT_V2_ENABLED),
		this.planAccessCheck.isAdmin$,
		this.planAccessCheck.accessLevel$,
	]).pipe(map(([flag, isAdmin, accessLevel]) => flag && isAdmin && accessLevel === PlanAccessLevel.live));

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
	public granularScaleControl = new FormControl<TicketScale>(TicketScale.Daily);
	protected TicketScale = TicketScale;
	// END PLACEHOLDER REPLACE AS PART OF TP-7317

	constructor(
		public planState: PlanState,
		public router: Router,
		private appState: AppState,
		private authService: AuthService,
		public intercomService: IntercomService,
		protected dialog: MatDialog,
		private featureFlagManager: FeatureFlagManager,
		public overviewProjectService: OverviewProjectService,
		private ticketEditService: TicketEditService,
		private organizationService: OrganizationService,
		private hoverOverPreferenceService: HoverOverUserPreferencesService,
		public userPreferenceStateService: UserPreferenceStateService,
		public planAccessCheck: PlanAccessCheck,
		private planTicketsFilterRemoteControlService: PlanTicketsFilterRemoteControlService,
		private planPrintPanelRemoteControlService: PlanPrintPanelRemoteControlService,
		protected granularEnabledService: GranularEnabledService
	) {
		this.undoEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.UNDO);
		this.analyticsEventsService = analyticsEventsService;
		this.widePullTicketEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.WIDE_PULL_TICKET);
		this.userId = this.appState.userSync.$id;
		this.isPlanPrintEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.PLAN_PRINT_ENABLED);
	}

	public ngOnInit() {
		this.planState.projectMembersApi.data.list$.subscribe((projectMembers: Map<string, ProjectMember>) => {
			this.projectMembers = projectMembers;
		});

		this.isOrgAdmin$ = this.organizationService.isOrgAdmin$;

		this.hoverOverPreferenceService
			.isHoverOverEnabled$(this.userId, this.planState.projectId)
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(this.isHoverOverEnabled$);

		// combine Print Plan menu item selection with the latest value of the Plan Print feature flag
		combineLatest([this.printPlanClicks$, this.isPlanPrintEnabled$])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(([event, isEnabled]) => {
				this.handlePrintPlanClick(event, isEnabled);
			});

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
		this.granularEnabledService.granularScale$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((granularScale) => this.granularScaleControl.setValue(granularScale, { emitEvent: false }));

		this.granularScaleControl.valueChanges.subscribe((granularScale) => {
			this.granularEnabledService.setRenderingMode(granularScale);
		});
		// END PLACEHOLDER REPLACE AS PART OF TP-7317
	}

	public ngOnDestroy(): void {
		this.printPlanClicks$.complete();
		this.isHoverOverEnabled$.complete();
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public onToggleHoverOver(): void {
		const isEnabled = !this.isHoverOverEnabled$.getValue();
		this.hoverOverPreferenceService.updateHoverOverUserProjectPreferences(
			this.userId,
			this.planState.projectId,
			{ isEnabled }
		);
		this.analyticsEventsService.setHoverVisibility(isEnabled);
	}

	public hidePlanStatus(): void {
		this.statusHidden$.next(true);
	}

	public showPlanStatus(): void {
		this.statusHidden$.next(false);
	}

	public navigate(path: string[]): void {
		this.router.navigate(path);
	}

	public goToMobile(): void {
		window.open('https://m.touchplan.io', '_self');
	}

	public logout(): void {
		this.authService.logout();
	}

	public getProjectMemberName(projectMemberId: string): string {
		const pm = this.projectMembers.get(projectMemberId);
		return pm && pm.view && pm.view.user
			? pm.view.user.firstName + ' ' + pm.view.user.lastName
			: 'No Active User';
	}

	public hideAttachments() {
		this.ticketEditService.hideTicketAttachments();
	}

	public openWideTicketChoicesModal() {
		this.planState.scopeSoup.toggleSidebar('pullTicketWidth');
	}

	public openAPISettings() {
		OrganizationAppEditorComponent.openOrganizationApiSettings(this.dialog);
	}

	public openNewReportsUI() {
		this.dialog.open(ReportsComponent, {
			data: {},
			disableClose: false,
		});
	}

	public showPinHistoryToggle(showPinHistory: boolean): void {
		this.userPreferenceStateService.showPinHistory = showPinHistory;
	}

	public viewCrewSizesToggle(viewCrewSizes: boolean): void {
		this.userPreferenceStateService.showLaborCount = viewCrewSizes;
		this.planState.scopeSoup.setViewLabor(viewCrewSizes);
	}

	public showAllDependenciesToggle(showAllDependencies: boolean): void {
		this.userPreferenceStateService.showAllDependencies = showAllDependencies;
	}

	public onOpenImportTickets() {
		openImportDialog(
			this.dialog,
			this.planState.projectId,
			this.planState.planId,
			this.planState.plansApi.data.list$,
			this.planState.locationsApi.data.list$,
			this.planState.rolesApi.data.list$,
			this.planState.tickets.list$,
			this.planState.variances.list$,
			this.planState.swimlanes.data.list$
		);
	}

	public onOverviewClick() {
		this.overviewProjectService.toggleOverview();
	}

	public lockPlan() {
		const { planId, plansApi } = this.planState;
		plansApi.planAccessApi.lockPlan(planId);
		analyticsEventsService.lockPlan(planId);
	}

	public unlockPlan() {
		const planId = this.planState.planId;
		this.planState.plansApi.planAccessApi.activatePlan(planId);
		analyticsEventsService.unlockPlan(planId);
	}

	/**
	 * Handles the click event from the Print Plan menu item.
	 * Emits the event to the click subject, which will be combined with the feature flag.
	 */
	public onClickPrintPlan(event: Event): void {
		this.printPlanClicks$.next(event);
	}

	/**
	 * Processes the Print Plan click and decides which action to take.
	 * If the feature flag is enabled, opens the plan print after closing the plan filter panel;
	 *  otherwise, runs the legacy print flow.
	 */
	private handlePrintPlanClick(event: Event, isEnabled: boolean): void {
		event.stopPropagation();

		if (isEnabled) {
			this.planTicketsFilterRemoteControlService.closePlanTicketsFilter();
			this.planPrintPanelRemoteControlService.openPrintPlantSettingsControl();
		} else {
			this.planState.scopeSoup.print.toggle();
			this.planState.scopeSoup.dispatchZoomToPrintSegment();
		}

		this.planActionsMenu?.close();
		this.planActionsMenuSmall?.close();
		this.planActionsMenuSmall = undefined;
		this.planActionsMenu = undefined;
	}

	/**
	 * Registers a reference to the dropdown menu component so it can be closed programmatically.
	 */
	public registerPlanActionsMenuSmall(ref: DropdownMenuComponent) {
		this.planActionsMenuSmall = ref;
	}
	public registerPlanActionsMenu(ref: DropdownMenuComponent) {
		this.planActionsMenu = ref;
	}
}
